import { supabase } from '@/integrations/supabase/client';
import type { Session } from '@supabase/supabase-js';

// Module-level lock to prevent concurrent refresh attempts
let refreshPromise: Promise<Session | null> | null = null;

/**
 * Ensures we have a valid authenticated session before performing writes.
 * Uses a single in-flight refresh lock to prevent race conditions.
 * If session is invalid and can't be refreshed, signs out cleanly.
 */
export async function ensureSession(): Promise<Session> {
  // First, try to get existing session
  const { data: { session }, error: getError } = await supabase.auth.getSession();
  
  if (session && !getError) {
    return session;
  }

  // No session - attempt refresh (with lock to prevent races)
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (error) {
          console.error('[ensureSession] Refresh failed:', error.message);
          // Clean sign-out so app returns to /auth cleanly
          await supabase.auth.signOut();
          return null;
        }
        return data.session;
      } finally {
        // Clear the lock after refresh completes
        refreshPromise = null;
      }
    })();
  }

  // Wait for the in-flight refresh
  const refreshedSession = await refreshPromise;
  
  if (!refreshedSession) {
    throw new Error('Session expired');
  }
  
  return refreshedSession;
}

/**
 * Check if user has a valid session without throwing.
 * Useful for queries where we want to fall back gracefully.
 */
export async function hasValidSession(): Promise<boolean> {
  try {
    await ensureSession();
    return true;
  } catch {
    return false;
  }
}
